import { X, Users } from "lucide-react";
import AuswahlFeld from "@/components/AuswahlFeld";

/**
 * MailAdressenFeld – „Weitere Kontakte“ beim Kunden (A-018 / BUT-0005, Teil 1; A-020 P4: Telefon, Notiz, Mail optional).
 *
 * Die Haupt-Mail bleibt das Feld „E-Mail“ des Kunden. Hier stehen weitere Adressen (z. B. Hausverwaltung, Mieter):
 * Adresse, Rolle (Auswahlliste „email_rolle“, pflegbar), Name/Zusatz und der Haken „immer in Kopie“.
 * Gespeichert wird im Kunden als `mail_adressen` = [{id, adresse, rolle, name, telefon, notiz, kopie}]; alle Mail-Wege der Suite
 * (Mailverlauf, Mail-Zentrale, Doppelte) lesen sie zentral über backend/utils/kunden_mailadressen.py.
 */
export const MAIL_ROLLEN_ERSATZ = ["Mieter", "Hausmeister", "Verwalter", "Hausverwaltung", "Eigentümer", "Kunde", "Büro", "Sonstige"];
const ADRESSE = /^[\w.+'-]+@[\w-]+(\.[\w-]+)+$/;

/** Fehlertext zur Liste (leer = in Ordnung): ungültige oder doppelte Adresse, auch doppelt zur Haupt-Mail. */
export const mailAdressenFehler = (form) => {
  const gesehen = new Set([(form?.email || "").trim().toLowerCase()].filter(Boolean));
  for (const z of form?.mail_adressen || []) {
    const a = (z.adresse || "").trim().toLowerCase();
    if (!a) continue; // Kontakt ohne Mail (nur Telefon) ist erlaubt
    if (!ADRESSE.test(a)) return `„${z.adresse.trim()}“ ist keine gültige Mail-Adresse.`;
    if (gesehen.has(a)) return `„${a}“ steht doppelt (Haupt-Mail oder weitere Adresse).`;
    gesehen.add(a);
  }
  return "";
};

const MailAdressenFeld = ({ form, setForm, testPrefix = "mailadr" }) => {
  const zeilen = form.mail_adressen || [];
  const onChange = (z) => setForm({ ...form, mail_adressen: z });
  const setze = (i, feld, wert) => onChange(zeilen.map((z, j) => (j === i ? { ...z, [feld]: wert } : z)));
  const fehler = mailAdressenFehler(form);
  const feldCls = "h-9 w-full min-w-0 rounded-sm border border-input bg-background px-2 text-sm";
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`${testPrefix}-bereich`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1.5"><Users className="w-4 h-4" />Weitere Kontakte</div>
      {zeilen.length === 0 && <p className="text-xs text-muted-foreground mb-2">Z. B. Mieter, Hausmeister, Verwalter – mit Telefon.</p>}
      <div className="space-y-2">
        {zeilen.map((z, i) => (
          <div key={i} className="rounded-sm border border-input bg-muted/20 p-2 space-y-1.5" data-testid={`${testPrefix}-zeile-${i}`}>
            <div className="flex items-center gap-1.5">
              <input value={z.adresse || ""} onChange={(e) => setze(i, "adresse", e.target.value)} placeholder="Mail-Adresse (kann leer bleiben)"
                inputMode="email" autoCapitalize="none" className={feldCls} data-testid={`${testPrefix}-adresse-${i}`} />
              <button type="button" onClick={() => onChange(zeilen.filter((_, j) => j !== i))} className="p-1.5 rounded hover:bg-muted text-muted-foreground shrink-0"
                title="Kontakt entfernen" data-testid={`${testPrefix}-weg-${i}`}><X className="w-4 h-4" /></button>
            </div>
            <AuswahlFeld docType="email_rolle" label="Rolle" value={z.rolle || ""} onChange={(v) => setze(i, "rolle", v)}
              ersatz={MAIL_ROLLEN_ERSATZ} leer="— Rolle wählen —" stichwoerter={false} testId={`${testPrefix}-rolle-${i}`} />
            <input value={z.name || ""} onChange={(e) => setze(i, "name", e.target.value)} placeholder="Name / Zusatz (z. B. Frau Meier)"
              className={feldCls} data-testid={`${testPrefix}-name-${i}`} />
            <input value={z.telefon || ""} onChange={(e) => setze(i, "telefon", e.target.value)} placeholder="Telefon (z. B. 0151 …)"
              inputMode="tel" className={feldCls} data-testid={`${testPrefix}-telefon-${i}`} />
            <input value={z.notiz || ""} onChange={(e) => setze(i, "notiz", e.target.value)} placeholder="Notiz (z. B. nur vormittags erreichbar)"
              className={feldCls} data-testid={`${testPrefix}-notiz-${i}`} />
            <label className="flex items-center gap-1.5 text-xs" title="Merkt diese Adresse als Kopie-Empfänger dieses Kunden">
              <input type="checkbox" checked={z.kopie === true} onChange={(e) => setze(i, "kopie", e.target.checked)} className="w-4 h-4"
                data-testid={`${testPrefix}-kopie-${i}`} /> immer in Kopie
            </label>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...zeilen, { adresse: "", rolle: "", name: "", telefon: "", notiz: "", kopie: false }])}
        className="mt-2 inline-flex items-center justify-center min-h-[36px] px-3 rounded-sm border border-input text-sm font-medium text-primary hover:bg-muted"
        data-testid={`${testPrefix}-dazu`}>+ Kontakt hinzufügen</button>
      {fehler && <p className="mt-1 text-xs text-red-700" data-testid={`${testPrefix}-fehler`}>{fehler}</p>}
    </div>
  );
};

export default MailAdressenFeld;
